/**
 * SuperAdmin Publisher Groups page.
 *
 * Lists every publisher group (hierarchy-aware), lets SuperAdmin create
 * + archive groups, and add/remove publisher / consumer / owner members.
 *
 * Guarded by SuperAdmins Cognito group; non-SuperAdmins redirect to dashboard.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Archive,
  ChevronRight,
  Home,
  Layers,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { useToast } from '../components/common/ToastContainer';
import { useAuth } from '../context/AuthContext';
import { SEEDED_PUBLISHER_GROUPS } from '../constants/tradingConstants';
import publisherGroupsService from '../services/publisherGroupsService';
import {
  GroupRole,
  PublisherGroup,
  PublisherGroupMember,
  PublisherGroupStats,
  PublisherGroupTreeNode,
} from '../types/publisherGroups';

// F7: derive from the single source of truth instead of duplicating the
// seeded slugs locally. `SEEDED_PUBLISHER_GROUPS` is a `readonly tuple`
// (`as const`), so spread into Set widens to the right element type.
const SEEDED_GROUP_IDS: Set<string> = new Set(SEEDED_PUBLISHER_GROUPS);
const ROLES: GroupRole[] = ['publisher', 'consumer', 'account_manager', 'owner'];

// Display labels for role chips/tabs. Slugs stay machine-friendly; labels
// are what SuperAdmins see in the UI.
const ROLE_LABEL: Record<GroupRole, string> = {
  publisher: 'Publishers',
  consumer: 'Consumers',
  account_manager: 'Account Managers',
  owner: 'Owners',
};
const ROLE_SINGULAR: Record<GroupRole, string> = {
  publisher: 'publisher',
  consumer: 'consumer',
  account_manager: 'account manager',
  owner: 'owner',
};

// Pull the server-supplied error string from a 4xx/5xx axios rejection.
// Falls back to axios's generic "Request failed with status code N" message
// otherwise. Lets operators see the precondition hint (e.g. "group has child
// groups — re-parent or delete them first") instead of just the status code.
function serverErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (data?.error) {
    if (Array.isArray(data.child_group_ids) && data.child_group_ids.length) {
      return `${data.error} (${data.child_group_ids.join(', ')})`;
    }
    if (typeof data.listings_using_group === 'number') {
      return `${data.error} (${data.listings_using_group})`;
    }
    return data.error;
  }
  return err?.message || fallback;
}

function formatIST(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch {
    return '—';
  }
}

const SuperAdminGroupsPage: React.FC = () => {
  const { isSuperAdmin, isLoading: authLoading } = useAuth();
  const toast = useToast();
  // ToastContext returns a new object identity on each render (its child toast
  // list mutates). Capturing it via a ref lets `fetchMembersAndStats` keep
  // stable useCallback identity, which in turn prevents the data-fetch
  // useEffect from re-firing every time a toast is shown. Without this, every
  // mutating action (add/remove member, archive) triggers TWO fetches: one
  // explicit + one from the useEffect re-firing on the toast-induced re-render.
  const toastRef = useRef(toast);
  useEffect(() => { toastRef.current = toast; }, [toast]);

  const [tree, setTree] = useState<PublisherGroupTreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [members, setMembers] = useState<PublisherGroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [stats, setStats] = useState<PublisherGroupStats | null>(null);
  const [activeRoleTab, setActiveRoleTab] = useState<GroupRole>('publisher');

  // Create-group dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    group_id: '',
    display_name: '',
    description: '',
    parent_group_id: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  // Archive confirmation
  const [pendingArchive, setPendingArchive] = useState<PublisherGroup | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);

  // Delete confirmation — requires typing the group slug to enable.
  // Distinct from archive (reversible soft-hide) since delete is irreversible
  // and frees the slug for reuse.
  const [pendingDelete, setPendingDelete] = useState<PublisherGroup | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Add member
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ user_id: '', role: 'publisher' as GroupRole });
  const [isAddingMember, setIsAddingMember] = useState(false);

  const fetchTree = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = (await publisherGroupsService.list({ tree: true })) as PublisherGroupTreeNode[];
      setTree(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load publisher groups');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Skip the fetch entirely until auth has loaded AND caller is SuperAdmin —
  // otherwise the initial render fires a request that 403s and surfaces as a
  // fetch error on first paint for non-SuperAdmin URL-hitters.
  useEffect(() => {
    if (authLoading || !isSuperAdmin) return;
    fetchTree();
  }, [fetchTree, authLoading, isSuperAdmin]);

  const fetchMembersAndStats = useCallback(async (groupId: string, role: GroupRole) => {
    setMembersLoading(true);
    try {
      const [m, s] = await Promise.all([
        publisherGroupsService.listMembers(groupId, role),
        publisherGroupsService.stats(groupId),
      ]);
      setMembers(m);
      setStats(s);
    } catch (err: any) {
      toastRef.current.showError(err.message || 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  }, []); // intentionally empty — toast accessed via stable ref above

  useEffect(() => {
    if (selectedGroupId) {
      fetchMembersAndStats(selectedGroupId, activeRoleTab);
    }
  }, [selectedGroupId, activeRoleTab, fetchMembersAndStats]);

  const flatGroups = useMemo<PublisherGroupTreeNode[]>(() => {
    const out: PublisherGroupTreeNode[] = [];
    const walk = (nodes: PublisherGroupTreeNode[]) => {
      for (const n of nodes) {
        out.push(n);
        if (n.children?.length) walk(n.children);
      }
    };
    walk(tree);
    return out;
  }, [tree]);

  const handleCreate = async () => {
    if (!createForm.group_id || !createForm.display_name) {
      toast.showError('group_id and display_name are required');
      return;
    }
    setIsCreating(true);
    try {
      await publisherGroupsService.create({
        group_id: createForm.group_id.trim().toLowerCase(),
        display_name: createForm.display_name.trim(),
        description: createForm.description.trim(),
        parent_group_id: createForm.parent_group_id || undefined,
      });
      toast.showSuccess(`Group "${createForm.group_id}" created`);
      setCreateOpen(false);
      setCreateForm({ group_id: '', display_name: '', description: '', parent_group_id: '' });
      fetchTree();
    } catch (err: any) {
      toast.showError(err.message || 'Failed to create group');
    } finally {
      setIsCreating(false);
    }
  };

  const handleArchive = async () => {
    if (!pendingArchive) return;
    setIsArchiving(true);
    try {
      await publisherGroupsService.archive(pendingArchive.group_id);
      toast.showSuccess(`Group "${pendingArchive.group_id}" archived`);
      setPendingArchive(null);
      if (selectedGroupId === pendingArchive.group_id) setSelectedGroupId(null);
      fetchTree();
    } catch (err: any) {
      toast.showError(serverErrorMessage(err, 'Failed to archive group'));
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await publisherGroupsService.delete(pendingDelete.group_id);
      toast.showSuccess(`Group "${pendingDelete.group_id}" deleted`);
      const deletedId = pendingDelete.group_id;
      setPendingDelete(null);
      setDeleteConfirmText('');
      if (selectedGroupId === deletedId) setSelectedGroupId(null);
      fetchTree();
    } catch (err: any) {
      toast.showError(serverErrorMessage(err, 'Failed to delete group'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedGroupId || !addMemberForm.user_id) return;
    setIsAddingMember(true);
    try {
      const { alreadyMember } = await publisherGroupsService.addMember(selectedGroupId, {
        user_id: addMemberForm.user_id.trim(),
        role: addMemberForm.role,
      });
      if (alreadyMember) {
        // Idempotent grant — re-issuing the same (user, role) on the same
        // group is a no-op (counter unchanged). Tell the operator that
        // explicitly rather than masquerading as a fresh add.
        toast.showWarning(
          `${addMemberForm.user_id} is already a ${ROLE_SINGULAR[addMemberForm.role]} of this group`,
        );
      } else {
        toast.showSuccess(`Added ${addMemberForm.user_id} as ${ROLE_SINGULAR[addMemberForm.role]}`);
      }
      setAddMemberOpen(false);
      setAddMemberForm({ user_id: '', role: activeRoleTab });
      fetchMembersAndStats(selectedGroupId, activeRoleTab);
    } catch (err: any) {
      toast.showError(err.message || 'Failed to add member');
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleRemoveMember = async (member: PublisherGroupMember) => {
    if (!selectedGroupId) return;
    try {
      await publisherGroupsService.removeMember(selectedGroupId, member.user_id, member.role);
      toast.showSuccess(`Removed ${member.user_id}`);
      fetchMembersAndStats(selectedGroupId, activeRoleTab);
    } catch (err: any) {
      toast.showError(err.message || 'Failed to remove member');
    }
  };

  const renderGroupNode = (node: PublisherGroupTreeNode, depth = 0): React.ReactNode => {
    const isSelected = node.group_id === selectedGroupId;
    const isArchived = node.status === 'ARCHIVED';
    const isSeeded = SEEDED_GROUP_IDS.has(node.group_id) || node.is_seeded;
    return (
      <React.Fragment key={node.group_id}>
        <button
          type="button"
          onClick={() => setSelectedGroupId(node.group_id)}
          className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
            isSelected
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
          } ${isArchived ? 'opacity-60' : ''}`}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
        >
          <span className="flex items-center gap-2 min-w-0">
            <ChevronRight className={`h-3 w-3 flex-shrink-0 ${node.children?.length ? '' : 'invisible'}`} />
            <span className="truncate">{node.display_name}</span>
            <code className="text-xs text-gray-400 dark:text-gray-500 truncate">{node.group_id}</code>
            {isSeeded && (
              <span className="text-[10px] uppercase tracking-wide font-medium text-green-600 dark:text-green-400">
                seeded
              </span>
            )}
            {isArchived && (
              <span className="text-[10px] uppercase tracking-wide font-medium text-gray-500">
                archived
              </span>
            )}
          </span>
        </button>
        {node.children?.map((c) => renderGroupNode(c, depth + 1))}
      </React.Fragment>
    );
  };

  const selectedGroup = selectedGroupId
    ? flatGroups.find((g) => g.group_id === selectedGroupId) || null
    : null;
  const selectedSeeded = selectedGroup
    ? SEEDED_GROUP_IDS.has(selectedGroup.group_id) || selectedGroup.is_seeded
    : false;

  // Auth gate: redirect non-SuperAdmins BEFORE rendering page chrome. Hooks
  // above always run (so order is stable); fetches are gated above.
  if (authLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  return (
    <StandardLayout>
      <PageHeader
        title="Publisher Groups"
        pageType="management"
        icon={<Layers className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Platform Owner' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchTree}
              disabled={isLoading}
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Group
            </Button>
          </div>
        }
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <span className="text-red-800 dark:text-red-200">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left pane: hierarchy */}
        <Card>
          <CardHeader>
            <CardTitle>Groups ({flatGroups.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {isLoading ? (
              <LoadingSpinner />
            ) : tree.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No groups yet.
              </div>
            ) : (
              <div className="space-y-1">
                {tree.map((n) => renderGroupNode(n))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Middle + right panes when a group is selected */}
        <div className="lg:col-span-2 space-y-4">
          {selectedGroup ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Listings" value={stats?.listing_count ?? 0} />
                <StatCard label="Publishers" value={stats?.member_counts?.publisher ?? 0} />
                <StatCard label="Consumers" value={stats?.member_counts?.consumer ?? 0} />
                <StatCard label="Subscribers" value={stats?.subscriber_total ?? 0} />
              </div>

              {/* Group detail + members */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle>{selectedGroup.display_name}</CardTitle>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <code>{selectedGroup.group_id}</code>
                        {selectedGroup.parent_group_id && (
                          <>
                            {' · parent: '}
                            <code>{selectedGroup.parent_group_id}</code>
                          </>
                        )}
                        {selectedGroup.created_at && (
                          <>{' · created '}{formatIST(selectedGroup.created_at)}</>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => {
                        setAddMemberForm({ user_id: '', role: activeRoleTab });
                        setAddMemberOpen(true);
                      }}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Member
                      </Button>
                      {!selectedSeeded && selectedGroup.status === 'ACTIVE' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPendingArchive(selectedGroup)}
                          title="Archive group (reversible soft-hide)"
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                      )}
                      {!selectedSeeded && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setDeleteConfirmText('');
                            setPendingDelete(selectedGroup);
                          }}
                          title="Delete group (irreversible)"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Role tabs */}
                  <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex gap-1 flex-wrap">
                    {ROLES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setActiveRoleTab(r)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          activeRoleTab === r
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {ROLE_LABEL[r]} ({stats?.member_counts?.[r] ?? 0})
                      </button>
                    ))}
                  </div>

                  {/* Members list */}
                  <div>
                    {membersLoading ? (
                      <LoadingSpinner />
                    ) : members.length === 0 ? (
                      <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No {ROLE_SINGULAR[activeRoleTab]}s in this group.
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {members.map((m) => (
                          <li key={`${m.role}#${m.user_id}`} className="px-6 py-3 flex items-center justify-between">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {m.full_name || m.email || m.user_id}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                <code>{m.user_id}</code>
                                {m.granted_by && <>{' · by '}{m.granted_by}</>}
                                {m.granted_at && <>{' · '}{formatIST(m.granted_at)}</>}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMember(m)}
                              title="Remove member"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
                <Users className="h-8 w-8 mx-auto mb-3 text-gray-400" />
                Select a group on the left to view its members and stats.
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create-group modal */}
      <Modal
        isOpen={createOpen}
        onClose={() => { if (!isCreating) setCreateOpen(false); }}
        ariaLabel="New Publisher Group"
        maxWidth="md"
      >
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New Publisher Group</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Group ID <span className="text-red-500">*</span>
            </label>
            <Input
              value={createForm.group_id}
              onChange={(e) => setCreateForm({ ...createForm, group_id: e.target.value })}
              placeholder="e.g. premium, beta, gold"
              disabled={isCreating}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Lowercase slug — letters, digits, dash, underscore. Cannot be changed later.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Display Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={createForm.display_name}
              onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
              placeholder="Premium Tier"
              disabled={isCreating}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Description
            </label>
            <Input
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              disabled={isCreating}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Parent Group (optional — for hierarchy)
            </label>
            <select
              value={createForm.parent_group_id}
              onChange={(e) => setCreateForm({ ...createForm, parent_group_id: e.target.value })}
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-3 py-2 text-sm"
              disabled={isCreating}
            >
              <option value="">None (root group)</option>
              {flatGroups
                .filter((g) => g.status === 'ACTIVE')
                .map((g) => (
                  <option key={g.group_id} value={g.group_id}>
                    {g.display_name} ({g.group_id})
                  </option>
                ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Consumer members of the parent automatically gain access to this group.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating ? 'Creating…' : 'Create Group'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add-member modal */}
      <Modal
        isOpen={addMemberOpen}
        onClose={() => { if (!isAddingMember) setAddMemberOpen(false); }}
        ariaLabel="Add Member"
        maxWidth="md"
      >
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Add Member to {selectedGroup?.display_name ?? ''}
          </h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              User ID <span className="text-red-500">*</span>
            </label>
            <Input
              value={addMemberForm.user_id}
              onChange={(e) => setAddMemberForm({ ...addMemberForm, user_id: e.target.value })}
              placeholder="Cognito sub (UUID)"
              disabled={isAddingMember}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Role
            </label>
            <select
              value={addMemberForm.role}
              onChange={(e) => setAddMemberForm({ ...addMemberForm, role: e.target.value as GroupRole })}
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-3 py-2 text-sm"
              disabled={isAddingMember}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_SINGULAR[r]}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              <strong>Publisher</strong>: can publish baskets to this group.{' '}
              <strong>Consumer</strong>: managed users with this role can be subscribed to listings in this group.{' '}
              <strong>Account manager</strong>: an AccountManager with this role can subscribe their managed users to listings in this group.{' '}
              <strong>Owner</strong>: reserved for future delegated administration.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setAddMemberOpen(false)} disabled={isAddingMember}>
              Cancel
            </Button>
            <Button onClick={handleAddMember} disabled={isAddingMember}>
              {isAddingMember ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Archive confirm */}
      <ConfirmDialog
        isOpen={pendingArchive !== null}
        title={`Archive "${pendingArchive?.display_name ?? ''}"?`}
        message="Archived groups are no longer selectable on publish dialogs. Existing listings tagged with this group will continue to work. Confirm archive?"
        confirmText={isArchiving ? 'Archiving…' : 'Archive'}
        variant="danger"
        onConfirm={handleArchive}
        onClose={() => setPendingArchive(null)}
      />

      {/* Delete confirm — irreversible, requires typing the slug */}
      <Modal
        isOpen={pendingDelete !== null}
        onClose={() => {
          if (!isDeleting) {
            setPendingDelete(null);
            setDeleteConfirmText('');
          }
        }}
        ariaLabel="Delete Publisher Group"
        maxWidth="md"
      >
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Delete "{pendingDelete?.display_name ?? ''}"?
          </h2>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-800 dark:text-red-200">
            <strong>Irreversible.</strong> Removes the group config and every membership row.
            Audit history is preserved. The slug becomes available for re-use — note that any
            future group created with the same slug will inherit the old audit trail.
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            The server will reject this if the group has published listings or child groups.
            Archive instead if you want a reversible soft-hide.
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Type <code className="text-red-600 dark:text-red-400">{pendingDelete?.group_id}</code> to confirm
            </label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={pendingDelete?.group_id ?? ''}
              disabled={isDeleting}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => { setPendingDelete(null); setDeleteConfirmText(''); }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={isDeleting || deleteConfirmText !== pendingDelete?.group_id}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </StandardLayout>
  );
};

const StatCard: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
      {label}
    </div>
    <div className="text-xl font-bold mt-1 text-blue-600 dark:text-blue-400 tabular-nums">
      {value.toLocaleString('en-IN')}
    </div>
  </div>
);

export default SuperAdminGroupsPage;
